﻿import type { OrderType } from 'apiConnector/blotters';
import { useNavigateTo } from 'hooks/useNavigateTo';
import type { IntlKey } from 'locales';
import { FC } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { useIntl } from 'react-intl';
import type { SelectedItem } from 'routes/secondaryMarketOrderBlotter/Blotter/SecondaryOrderBlotter.connect';
import { LinkButton } from 'routes/secondaryMarketOrderBlotter/SidePanel/Buttons';
import type { CancellationStatus } from 'state/secondaryMarket/shared/cancellation/cancellation.model';
import type { MakeRequired } from 'types/utils';

export interface SecondaryOrderSidePanelFooterProps {
  selected: MakeRequired<SelectedItem, 'orderType'>;
  isSecondaryMarketPostTradeRedirectionAllowed: boolean;
  isSecondaryMarketOrderCancellationAllowed: boolean;
  isSecondaryOrderBookEnabled: boolean;
  orderCancellationStatus: CancellationStatus | undefined;
  onOpenSecondaryMarketPostTrade: (orderId: string, tabName: string) => void;
  onOpenNewOrder: (orderId: string) => void;
  onCancellationWorkflowInitiated: (orderId: string) => void;
  onInstantBookWorkflowInitiated: (orderId: string) => void;
}
type OrderTypeConfig = {
  tabName: IntlKey;
  amendBtnName: IntlKey;
  openNewBtnName: IntlKey;
  cancellationBtnName: IntlKey;
};
const configByOrderTypeMap: Record<OrderType, OrderTypeConfig> = {
  Live: {
    amendBtnName: 'secondaryMarketOrderBlotter.buttons.modifyLiveTrade',
    openNewBtnName: 'secondaryMarketOrderBlotter.buttons.openLiveTrade',
    tabName: 'secondaryMarket.postEventWorkspace.live.tab',
    cancellationBtnName: 'secondaryMarketOrderBlotter.buttons.cancelLiveTrade',
  },
  'Order on close': {
    amendBtnName: 'secondaryMarketOrderBlotter.buttons.modifyOrderOnClose',
    openNewBtnName: 'secondaryMarketOrderBlotter.buttons.openCPOrder',
    tabName: 'secondaryMarket.postEventWorkspace.order.tab',
    cancellationBtnName: 'secondaryMarketOrderBlotter.buttons.cancelOrder',
  },
  'Limit order': {
    amendBtnName: 'secondaryMarketOrderBlotter.buttons.modifyLimitOrder',
    openNewBtnName: 'secondaryMarketOrderBlotter.buttons.openLimitOrder',
    tabName: 'secondaryMarket.postEventWorkspace.limitOrder.tab',
    cancellationBtnName: 'secondaryMarketOrderBlotter.buttons.cancelLimitOrder',
  },
};
export const SecondaryOrderSidePanelFooter: FC<SecondaryOrderSidePanelFooterProps> = ({
  selected,
  isSecondaryMarketOrderCancellationAllowed,
  isSecondaryMarketPostTradeRedirectionAllowed,
  isSecondaryOrderBookEnabled,
  orderCancellationStatus,
  onCancellationWorkflowInitiated,
  onInstantBookWorkflowInitiated,
  onOpenNewOrder,
  onOpenSecondaryMarketPostTrade,
}) => {
  const { formatMessage } = useIntl();

  const shouldDisplayInstantBookButton = selected.orderType === 'Live';

  const isInstantBookButtonDisabled =
    shouldDisplayInstantBookButton && (!isSecondaryOrderBookEnabled || selected.orderStatus !== 'Waiting');

  const isOrderCancellationButtonDisabled =
    !isSecondaryMarketOrderCancellationAllowed ||
    orderCancellationStatus === 'IN_PROGRESS' ||
    selected.orderStatus === 'Cancelled';

  const navigateToPostTrade = useNavigateTo('/secondary-market-post-event-workspace');

  const handleSecondaryMarketPostTradeEditClicked = () => {
    navigateToPostTrade();
    onOpenSecondaryMarketPostTrade(
      selected.orderId,
      formatMessage({ id: configByOrderTypeMap[selected.orderType].tabName }, { orderId: selected.orderId }),
    );
  };

  return (
    <div className="d-flex">
      <div className="btn-group my-1 w-100">
        <LinkButton
          e2e="amend-order-btn"
          labelKey={configByOrderTypeMap[selected.orderType].amendBtnName}
          icon="create"
          className="btn btn-xl ms-1 me-0 w-100"
          onClick={handleSecondaryMarketPostTradeEditClicked}
          isDisabled={!isSecondaryMarketPostTradeRedirectionAllowed}
        />

        {selected.canOpenOrder && (
          <Dropdown drop="up">
            <Dropdown.Toggle
              className="btn btn-xl dropdown-toggle dropdown-toggle-split ms-0 me-1 pt-3 h-100 border-0 btn-discreet-primary"
              data-e2e="order-actions-toggle-btn"
            />
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => onOpenNewOrder(selected.orderId)} data-e2e="open-new-order-btn">
                {formatMessage({
                  id: configByOrderTypeMap[selected.orderType].openNewBtnName,
                })}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        )}
      </div>
      {shouldDisplayInstantBookButton && (
        <button
          data-e2e="instant-book-button"
          className="btn btn-xl btn-discreet-info m-1"
          title={formatMessage({ id: 'secondaryMarketOrderBlotter.buttons.book' })}
          type="button"
          onClick={() => onInstantBookWorkflowInitiated(selected.orderId)}
          disabled={isInstantBookButtonDisabled}
        >
          <i className="icon">book</i>
        </button>
      )}

      <button
        data-e2e="order-cancellation-button"
        className="btn btn-discreet-danger btn-xl m-1"
        title={formatMessage({
          id: configByOrderTypeMap[selected.orderType].cancellationBtnName,
        })}
        type="button"
        onClick={() => onCancellationWorkflowInitiated(selected.orderId)}
        disabled={isOrderCancellationButtonDisabled}
      >
        <i className="icon">cancel</i>
      </button>
    </div>
  );
};
